import * as THREE from 'three';
import type { Model } from '../models/types.ts';
import { Kit } from '../seasons/kit.ts';
import { districtMaterial, type DistrictUniforms } from './districts.ts';

/**
 * The saberes' still pieces round the keep (docs/SABERES.md §4.3, §4.5, §6): the lectern of lore, the stone of names,
 * the alarm's bell frame, the lullaby's bowls and the song's campfire, all in one merged mesh painted with vertex
 * colours, in the districts' program (engine/districts.ts: no new shader). Each was its own baked trophy with its own
 * meshes and programs; together they were 15 draw calls and 9 programs on a bastion that had none of them before
 * (QA, 2026-11-04). Now one call, one more for the alarm's bell (the only part that moves, swung by its matrix), and
 * their glows are the shared glow layer's, given back to the view every frame.
 *
 * Built from the pieces' ordinary models (models/saberes.ts, saberes2.ts): what lights them (a self-lit paint) becomes
 * the vertex glow, their glow sprites become glows. Rebuilt only when a piece, its place or its count changes.
 */

export interface YardPiece {
  /** What the piece is and where: the same again leaves the mesh as it is. */
  key: string;
  make: () => Model;
  /** Its placement in the world. */
  m: THREE.Matrix4;
}

export interface YardGlow {
  p: THREE.Vector3;
  color: THREE.Color;
  size: number;
  op: number;
}

const tmp = new THREE.Matrix4();
const v = new THREE.Vector3();
const sc = new THREE.Vector3();
const q = new THREE.Quaternion();
const zAxis = new THREE.Vector3(0, 0, 1);

/** How much a self-lit paint glows in the merged mesh (its emissive intensity, damped: the shader adds 2.2×). */
const glowOf = (m: THREE.MeshStandardMaterial): number => (m.emissive && m.emissive.getHex() !== 0 && m.emissiveIntensity > 0.2 ? Math.min(0.6, m.emissiveIntensity * 0.32) : 0);

export class YardProps {
  readonly group = new THREE.Group();
  readonly glows: YardGlow[] = [];
  private u: DistrictUniforms = { uRise: { value: 1 }, uUnfurl: { value: 1 }, uTime: { value: 0 }, uSway: { value: 0 } };
  private mat = districtMaterial(this.u);
  private mesh: THREE.Mesh | null = null;
  /** The alarm's bell: its own small mesh swinging on its pivot, and the frame it hangs in. */
  private bell: { mesh: THREE.Mesh; pivot: THREE.Matrix4; tick: (n: number) => number; shown: number } | null = null;
  private made = '';

  constructor() {
    this.group.name = 'saberes-yard';
    this.group.matrixAutoUpdate = false;
  }

  get empty(): boolean {
    return !this.mesh;
  }

  /** Puts up `pieces` (null or none takes all down). */
  set(pieces: YardPiece[]): void {
    const made = pieces.map((p) => `${p.key}@${p.m.elements.map((e) => e.toFixed(3)).join(',')}`).join('|');
    if (made === this.made) return;
    this.made = made;
    this.clear();
    if (pieces.length === 0) return;
    const kit = new Kit({ aTop: -1, aGlow: 0, aPh: 0 });
    const bellKit = new Kit({ aTop: -1, aGlow: 0, aPh: 0 });
    for (const piece of pieces) {
      const model = piece.make();
      model.tick(0);
      model.root.matrixAutoUpdate = false;
      model.root.matrix.copy(piece.m);
      model.root.updateMatrixWorld(true);
      // The bell swings on its own: what hangs under its pivot goes in its own mesh, in the pivot's frame.
      const bellNode = model.root.getObjectByName('bell');
      const pivotInv = bellNode ? new THREE.Matrix4().copy(bellNode.matrixWorld).invert() : null;
      const under = (o: THREE.Object3D): boolean => {
        for (let p: THREE.Object3D | null = o; p; p = p.parent) if (p === bellNode) return true;
        return false;
      };
      model.root.traverse((o) => {
        if (!o.visible) return;
        const sprite = o as THREE.Sprite;
        if (sprite.isSprite) {
          const sm = sprite.material as THREE.SpriteMaterial;
          o.matrixWorld.decompose(v, q, sc);
          this.glows.push({ p: v.clone(), color: sm.color.clone(), size: sc.x, op: sm.opacity });
          return;
        }
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh) return;
        const mat = mesh.material as THREE.MeshStandardMaterial;
        if (!mat || mat.transparent) return;
        const vals = { aGlow: glowOf(mat) };
        if (bellNode && pivotInv && under(o)) bellKit.add(mesh.geometry, mat.color, tmp.multiplyMatrices(pivotInv, mesh.matrixWorld), undefined, vals);
        else kit.add(mesh.geometry, mat.color, mesh.matrixWorld, undefined, vals);
      });
      if (bellNode) {
        const bm = bellKit.build(this.mat, 'alarm-bell');
        if (bm) {
          bm.matrixAutoUpdate = false;
          bm.matrix.copy(bellNode.matrixWorld);
          // Not updated by itself (its matrix is set by hand): three takes it in only when told.
          bm.matrixWorldNeedsUpdate = true;
          this.group.add(bm);
          const tick = model.tick;
          const node = bellNode;
          this.bell = { mesh: bm, pivot: bellNode.matrixWorld.clone(), tick: (n) => (tick(n), node.rotation.z), shown: 0 };
        }
      }
      model.root.traverse((o) => (o as THREE.Mesh).geometry?.dispose());
    }
    this.mesh = kit.build(this.mat, 'saberes-yard');
    if (this.mesh) {
      this.mesh.matrixAutoUpdate = false;
      this.group.add(this.mesh);
    }
  }

  /** The bell's swing by the view's clock (its matrix only when the angle moved). */
  frame(now: number): void {
    const b = this.bell;
    if (!b) return;
    const a = b.tick(Math.floor(now * 12));
    if (a === b.shown) return;
    b.shown = a;
    b.mesh.matrix.multiplyMatrices(b.pivot, tmp.makeRotationAxis(zAxis, a));
    b.mesh.matrixWorldNeedsUpdate = true;
  }

  private clear(): void {
    for (const m of [this.mesh, this.bell?.mesh]) {
      if (!m) continue;
      this.group.remove(m);
      m.geometry.dispose();
    }
    this.mesh = null;
    this.bell = null;
    this.glows.length = 0;
  }
}
